import * as React from "react";
import {inject, observer} from "mobx-react";
import {observable} from "mobx";
import {IconButton} from "../../../../widgets/button/IconButton";
import {SelectInput} from "../../../../widgets/input/select/SelectInput";
import {FieldDataType} from "../../../../../generated/api/base";
import type {AppState} from "../../../../../data/state/AppState";
import {FieldDataTypes} from "../../../../../data/models/field/FieldDataTypes";
import type {IFilterOperatorConfig} from "../../../../../data/models/filter/operator/FilterOperators";
import {FilterOperators} from "../../../../../data/models/filter/operator/FilterOperators";
import type {FilterOperator} from "../../../../../data/models/filter/operator/FilterOperator";
import type {IFilter} from "../../../../../data/models/filter/Filter";
import type {IFieldPointer} from "../../../../../data/models/field/Field";
import {SVGIcon} from "../../../../widgets/button/SVGIcon";
import {ReactUtils} from "../../../../utils/ReactUtils";

interface IAdvancedFilterFieldProps {
	readonly fieldRefIds: IFieldPointer[];
	readonly filter: IFilter;
	readonly onChangeField: (field: IFieldPointer) => void;
	readonly onChangeOperator: (operator: FilterOperator) => void;
	readonly onChangeParams: (params: any[]) => void;
	readonly onRemove: () => void;
	readonly operatorFilter?: (operator: FilterOperator) => boolean;
	readonly appState?: AppState;
	readonly linkedFields?: boolean;
	readonly children: React.ReactNode;
}

interface IAdvancedFilterFieldState {
	focus: boolean;
}

@inject("appState")
@observer
export class AdvancedFilterField extends React.PureComponent<IAdvancedFilterFieldProps, IAdvancedFilterFieldState> {
	@observable
	private _appState: AppState;

	@observable
	private _linkedFields: boolean;

	constructor(props: IAdvancedFilterFieldProps) {
		super(props);
		this._appState = props.appState;
		this._linkedFields = props.linkedFields || false;

		this.state = {
			focus: false,
		};
	}

	private renderField = (fieldRefId: string) => {
		return (
			<div className="hbox alignCenter filterField">
				{this._linkedFields && <SVGIcon icon="dol-field" />}
				<span>{this._appState.actions.getFieldTitle(fieldRefId)}</span>
			</div>
		);
	};

	private onFocus = (value: boolean) => {
		this.setState({focus: value});
	};

	public override componentDidUpdate(prevProps: IAdvancedFilterFieldProps) {
		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}

		if (prevProps.linkedFields !== this.props.linkedFields) {
			this._linkedFields = this.props.linkedFields;
		}
	}

	public override render() {
		const {onRemove, appState, filter, fieldRefIds, onChangeField, onChangeOperator, onChangeParams, operatorFilter} = this.props;

		const {focus} = this.state;

		const selectedFieldRefId = fieldRefIds.includes(filter.field) ? filter.field : fieldRefIds[0];
		const selectedField = appState.actions.getFieldByRefId(selectedFieldRefId);

		//const fields        = this.getFields();
		//const selectedField = fields.find(field => field.refId === filter.field) || fields[0];

		let operators = FieldDataTypes.map[selectedField.dataType].operators(selectedField.dataTypeSettings);

		if (operatorFilter) {
			operators = operators.filter(operatorFilter);
		}

		const operator = FilterOperators.map[filter.operator] as IFilterOperatorConfig;
		const ParamControl = operator ? operator.control : null;

		return (
			<div className={ReactUtils.cls("AdvancedFilterField hbox", {focus})}>
				<div className="criterias">
					{this.props.children}
					<SelectInput
						sort={true}
						options={fieldRefIds}
						selected={selectedFieldRefId}
						onChange={(fieldRefId) => onChangeField(fieldRefId)}
						render={this.renderField}
						onFocus={this.onFocus}
						stringifyOption={(fieldRefId) => appState.actions.getFieldTitle(fieldRefId)}
					/>
					<SelectInput
						options={operators}
						selected={filter.operator}
						onChange={(operator) => onChangeOperator(operator)}
						render={(operation) => {
							const op = FilterOperators.map[operation];
							let label = op.label;

							if (selectedField.dataType === FieldDataType.Boolean) {
								label = appState.actions.getBooleanFieldCustomLabelSettingsByFilterOperator(selectedField, op.id);
							}

							return label;
						}}
						onFocus={this.onFocus}
					/>
					{ParamControl && (
						<ParamControl
							param={filter.param}
							onChange={onChangeParams}
							onFocus={this.onFocus}
							field={selectedField}
							format={selectedField?.dataTypeSettings?.format}
						/>
					)}
				</div>
				<IconButton
					title="Delete"
					icon="close"
					className="delete"
					onClick={onRemove}
				/>
			</div>
		);
	}
}
